import { useCallback, useState } from "react";
import { API } from "@/lib/api";

const parseEvents = (chunk) =>
  chunk.split("\n\n").map((b) => b.replace(/^data: /, "").trim()).filter(Boolean).map((s) => {
    try {
      return JSON.parse(s);
    } catch {
      return null;
    }
  }).filter(Boolean);

/** Streams answers from the AI home assistant. onNeedAccount(msg, retry) fires when the free try is used up. */
export function useHomeChat(listingKey, onNeedAccount) {
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/ai/chat/${listingKey}`, { credentials: "include" });
      const d = await r.json();
      setMessages(Array.isArray(d?.messages) ? d.messages : []);
    } catch {
      setMessages([]);
    }
  }, [listingKey]);

  const send = async (text) => {
    const q = text.trim();
    if (!q || busy) return;
    setBusy(true);
    setStatus("Thinking…");
    setMessages((m) => [...m, { role: "user", text: q }, { role: "assistant", text: "", pending: true }]);
    const patchLast = (fn) => setMessages((m) => m.map((x, i) => (i === m.length - 1 ? fn(x) : x)));
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ listing_key: listingKey, message: q }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setMessages((m) => m.slice(0, -2));
        if (d?.detail?.code === "register_required") onNeedAccount(d.detail.msg, () => send(q));
        else patchLast((x) => x);
        if (d?.detail?.code !== "register_required") {
          setMessages((m) => [...m, { role: "user", text: q }, { role: "assistant", text: typeof d?.detail === "string" ? d.detail : "Something went wrong. Please try again.", error: true }]);
        }
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const cut = buf.lastIndexOf("\n\n");
        if (cut < 0) continue;
        const events = parseEvents(buf.slice(0, cut));
        buf = buf.slice(cut + 2);
        events.forEach((ev) => {
          if (ev.status) setStatus(ev.status);
          if (ev.t) {
            setStatus("");
            patchLast((x) => ({ ...x, text: x.text + ev.t }));
          }
          if (ev.error) patchLast((x) => ({ ...x, text: ev.error, error: true }));
        });
      }
      patchLast((x) => ({ ...x, pending: false }));
    } catch {
      patchLast((x) => ({ ...x, text: "Connection lost. Please try again.", error: true, pending: false }));
    } finally {
      setBusy(false);
      setStatus("");
    }
  };

  return { messages, status, busy, send, load };
}
